// Central client-side progress store. All localStorage reads/writes for
// in-flight code, solved markers, and XP events funnel through this module so
// the Phase B backend swap is a single-file change.

export type EditorFile = {
  name: string;
  code: string;
  readonly?: boolean;
  isEntry?: boolean;
};

export type Language = 'python' | 'javascript' | 'typescript';

// ── Storage keys ──────────────────────────────────────────────────────────

const CODE_PREFIX = 'sb.code.v1.';
const SOLVED_KEY  = 'sb.solved.v1';
const XP_KEY      = 'sb.xp.events.v1';

const codeKey = (problemId: string, lang: Language) =>
  `${CODE_PREFIX}${problemId}.${lang}`;

// ── Safe localStorage helpers ─────────────────────────────────────────────

function safeGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function safeSet(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err) {
    // QuotaExceededError — evict bottom 25% of code entries by updatedAt and
    // retry once. Falls through to false on persistent failure.
    if (err instanceof Error && /quota|storage/i.test(err.name + err.message)) {
      evictOldestCode(0.25);
      try { localStorage.setItem(key, value); return true; } catch { /* fallthrough */ }
    }
    return false;
  }
}

function safeRemove(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

function evictOldestCode(fraction: number): void {
  try {
    const entries: { key: string; updatedAt: number }[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(CODE_PREFIX)) continue;
      try {
        const parsed = JSON.parse(localStorage.getItem(k) ?? '{}');
        entries.push({ key: k, updatedAt: Number(parsed.updatedAt) || 0 });
      } catch { entries.push({ key: k, updatedAt: 0 }); }
    }
    entries.sort((a, b) => a.updatedAt - b.updatedAt);
    const cut = Math.max(1, Math.floor(entries.length * fraction));
    for (let i = 0; i < cut; i++) safeRemove(entries[i].key);
  } catch { /* ignore */ }
}

// ── Code persistence ──────────────────────────────────────────────────────

interface StoredCode {
  files: EditorFile[];
  updatedAt: number;
}

export function loadCode(problemId: string, lang: Language): EditorFile[] | null {
  const raw = safeGet(codeKey(problemId, lang));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredCode;
    if (!parsed || !Array.isArray(parsed.files)) return null;
    return parsed.files;
  } catch { return null; }
}

export function saveCode(problemId: string, lang: Language, files: EditorFile[]): void {
  const payload: StoredCode = { files, updatedAt: Date.now() };
  safeSet(codeKey(problemId, lang), JSON.stringify(payload));
}

export function clearCode(problemId: string, lang?: Language): void {
  if (lang) {
    safeRemove(codeKey(problemId, lang));
    return;
  }
  safeRemove(codeKey(problemId, 'python'));
  safeRemove(codeKey(problemId, 'javascript'));
}

// Reconcile a fresh starter scaffold with any persisted edits — keeps the
// starter's readonly/isEntry flags and any new files added since last save,
// while overlaying user code onto editable files.
export function hydrateFiles(
  problemId: string,
  lang: Language,
  starter: EditorFile[],
): EditorFile[] {
  const stored = loadCode(problemId, lang);
  if (!stored) return starter;
  const byName = new Map(stored.map(f => [f.name, f.code]));
  return starter.map(f => {
    if (f.readonly) return f;
    const persisted = byName.get(f.name);
    return persisted !== undefined ? { ...f, code: persisted } : f;
  });
}

// True if the user has ever opened this problem in the IDE — used by
// realm/map views that need a cheap "in progress" signal without loading the
// starter scaffold for every problem in a realm.
export function hasSavedCode(problemId: string): boolean {
  return safeGet(codeKey(problemId, 'python')) !== null
      || safeGet(codeKey(problemId, 'javascript')) !== null;
}

// True if any editable file's content differs from its fresh starter — used to
// derive the "attempted" state on the skill page without storing a flag.
export function isAttempted(problemId: string, starter: EditorFile[]): boolean {
  const langs: Language[] = ['python', 'javascript'];
  for (const lang of langs) {
    const stored = loadCode(problemId, lang);
    if (!stored) continue;
    const starterByName = new Map(starter.map(f => [f.name, f.code]));
    for (const f of stored) {
      const orig = starterByName.get(f.name);
      if (orig === undefined) continue;
      if ((f.code ?? '').trim() !== (orig ?? '').trim()) return true;
    }
  }
  return false;
}

// ── Solved markers ────────────────────────────────────────────────────────

export interface SolvedRecord { solvedAt: number; lang: Language; }
type SolvedMap = Record<string, SolvedRecord>;

function loadSolvedMap(): SolvedMap {
  const raw = safeGet(SOLVED_KEY);
  if (!raw) return {};
  try { return JSON.parse(raw) as SolvedMap; } catch { return {}; }
}

export function markSolved(problemId: string, lang: Language): void {
  const map = loadSolvedMap();
  // Keep earliest solve timestamp (don't overwrite if already solved).
  if (!map[problemId]) map[problemId] = { solvedAt: Date.now(), lang };
  safeSet(SOLVED_KEY, JSON.stringify(map));
}

export function getSolvedSet(): Set<string> {
  return new Set(Object.keys(loadSolvedMap()));
}

export function isSolved(problemId: string): boolean {
  return Boolean(loadSolvedMap()[problemId]);
}

// ── XP events (drives heatmap, sparkline, recent solves) ─────────────────

export interface XpEvent {
  problemId: string;
  xp: number;
  ts: number;          // ms since epoch
}

export function recordXpEvent(problemId: string, xp: number): void {
  const raw = safeGet(XP_KEY);
  let events: XpEvent[] = [];
  if (raw) {
    try { events = JSON.parse(raw) as XpEvent[]; } catch { events = []; }
  }
  events.push({ problemId, xp, ts: Date.now() });
  // Cap at ~1 year of daily events; trim oldest first.
  if (events.length > 1000) events = events.slice(events.length - 1000);
  safeSet(XP_KEY, JSON.stringify(events));
}

export function loadXpEvents(): XpEvent[] {
  const raw = safeGet(XP_KEY);
  if (!raw) return [];
  try { return JSON.parse(raw) as XpEvent[]; } catch { return []; }
}

// ── Aggregations (used by ProfilePage) ────────────────────────────────────

// Returns a Map from YYYY-MM-DD (local) → total XP that day.
export function xpByDay(events: XpEvent[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of events) {
    const d = new Date(e.ts);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    out.set(key, (out.get(key) ?? 0) + e.xp);
  }
  return out;
}

// Returns last `days` calendar-day buckets ending today (oldest first).
export function lastNDayXp(events: XpEvent[], days: number): { date: string; xp: number }[] {
  const byDay = xpByDay(events);
  const out: { date: string; xp: number }[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    out.push({ date: key, xp: byDay.get(key) ?? 0 });
  }
  return out;
}
